/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-assessments.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import './admin-studies-main-styles.scss';
import AssessmentDialog from '@components/admin/assessments-dialog';
import AdminStudyAssessmentsGrid from '@components/admin/study-assessments-grid/admin-study-assessments-grid';
import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import PageTitle from '@components/core/page-title/page-title';
import Loading from '../../../components/core/loading';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { addCRFNameToAssessment } from '@utils/filter';
import NotificationService from '@utils/notification';
import { getStudy, getStudyVariables } from '@queries/generated/studies.generated';
import {
    studyCrfs,
    studyCrfsVariables,
    studyCrfAssessments,
    studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment,
    studyCrfAssessmentsVariables,
} from '@queries/generated/study-crfs.generated';
import { GET_STUDY } from '@queries/studies';
import { GET_STUDY_CRF_ASSESSMENTS, GET_STUDY_CRFS } from '@queries/study-crfs';

const AdminStudyAssessments = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const { state: localState } = useLocalState();
    const studyId = localState.selectedStudy?.id;
    const canEdit = user.canWriteSystemAdminLevel();
    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);
    // NOTE: I added the 'no-cache' option because for some reason it was returning wrong(cached) crfRules array.
    // It might be fixed by updating apollo/client(which last published 3 years ago) to @apollo/client
    const {
        data: assessmentsData,
        loading: assessmentsLoading,
        refetch,
    } = useQuery<studyCrfAssessments, studyCrfAssessmentsVariables>(GET_STUDY_CRF_ASSESSMENTS, {
        variables: { studyId: studyId ?? null },
    });
    const [loadData, { data: crfsData, loading: crfsLoading }] = useLazyQuery<studyCrfs, studyCrfsVariables>(
        GET_STUDY_CRFS,
    );
    const [loadStudy, { data: studyData, loading: studyLoading }] = useLazyQuery<getStudy, getStudyVariables>(
        GET_STUDY,
    );
    const [newAssessments, setNewAssesments] = useState([]);
    const addAssesmentsOpen = useBoolean(false);
    const [currentAssessment, setCurrentAssessment] =
        useState<studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment | null>(null);
    const assessmentAssociation = studyData?.study?.assessmentAssociation;

    useEffect(() => {
        if (studyId) {
            loadData({ variables: { studyId } });
            loadStudy({ variables: { id: studyId } });
        }
    }, [studyId]);

    useEffect(() => {
        if (assessmentsData?.crfAssessments?.length) {
            let assessmentsArray = addCRFNameToAssessment(
                assessmentsData?.crfAssessments,
                crfsData?.crfs,
                assessmentAssociation,
            );
            const newData = restructuredUpdatedCreatedBy(assessmentsArray);
            assessmentsArray = orderBy(newData, [{ field: 'name', dir: 'asc' }]) ?? [];
            setNewAssesments(assessmentsArray);
        } else {
            setNewAssesments(null);
        }
    }, [assessmentsData, crfsData]);

    if (!studyId || assessmentsLoading || crfsLoading || studyLoading) return <Loading />;

    return (
        <>
            <PageTitle text={t('admin:manageAssessments')} />
            <div className="info-box-wrapper">
                <DialogInformationBox
                    topText={t(
                        assessmentAssociation
                            ? 'admin:manageAssessmentsAssociationTrue'
                            : 'admin:manageAssessmentsAssociationFalse',
                    )}
                />
            </div>
            <div className="admin-study-main-container main-crf-container">
                {addAssesmentsOpen.value && (
                    <AssessmentDialog
                        isSysAdmin={isSysAdmin}
                        currentAssessment={currentAssessment}
                        assessments={newAssessments}
                        close={(action: string | undefined) => {
                            if (action === 'error') {
                                NotificationService.showNotification(t('errorMessage'));
                            } else {
                                refetch({ studyId });
                                if (action === 'success') {
                                    NotificationService.showNotification(
                                        t('admin:successfulCreateSimplyAssessment', {
                                            id: currentAssessment?.recordId || null,
                                        }),
                                    );
                                }
                                if (action === 'edit') {
                                    NotificationService.showNotification(
                                        t('admin:successfulUpdateAssessment', {
                                            id: currentAssessment?.recordId || null,
                                        }),
                                    );
                                }
                                setCurrentAssessment(null);
                                addAssesmentsOpen.setFalse();
                            }
                        }}
                    />
                )}
                <AdminStudyAssessmentsGrid
                    assessments={newAssessments}
                    canEdit={canEdit}
                    isSysAdmin={isSysAdmin}
                    showAddAssessmentsDialog={addAssesmentsOpen.setTrue}
                    setCurrentAssessment={setCurrentAssessment}
                />
            </div>
        </>
    );
};

export default AdminStudyAssessments;
